/**
 * The DEFAULT carousel source: request attachments (GitLab #1539).
 *
 * This is the behaviour the carousel had hard-wired before its source became
 * injectable, moved here verbatim so the request attachment list, the grouped
 * list, Summary and the image refs do not change:
 *
 *  · bytes through `getAttachment(id)`, a `success: false` result being an error;
 *  · footer thumbnails through `getAttachmentThumbnail`, falling back to the
 *    full file for a legacy image with no stored thumbnail;
 *  · the unsupported page's Download through `attachmentService.downloadAttachment`.
 *
 * `toCarouselMedia` is the one place a `SavedAttachment` becomes a carousel
 * item; `useAttachmentCarousel` applies it, so every existing call site keeps
 * passing `SavedAttachment[]`.
 */
import {
  SavedAttachment,
  attachmentService,
  getAttachment,
  getViewableFileUrl,
} from '../../../services/attachmentService';
import { getSectionLabel } from '../../../utils/sectionLabel';

import type { CarouselMedia, CarouselSource } from './carouselMedia';

export const toCarouselMedia = (
  attachment: SavedAttachment,
): CarouselMedia => ({
  id: String(attachment.id),
  name: attachment.attachmentName,
  mimeType: attachment.mimeType,
  sizeBytes: attachment.fileSizeBytes,
  thumbnailUri:
    attachment.thumbnail && attachment.thumbnailMimeType
      ? getViewableFileUrl(attachment.thumbnail, attachment.thumbnailMimeType)
      : undefined,
  sectionLabel: getSectionLabel(attachment.requestSection) || undefined,
});

/** Request attachment ids are numeric; the carousel carries them as strings. */
const attachmentId = (item: CarouselMedia): number => Number(item.id);

export const requestAttachmentCarouselSource: CarouselSource = {
  load: async item => {
    const result = await getAttachment(attachmentId(item));
    if (result.success && result.attachment?.attachmentFile) {
      return {
        base64: result.attachment.attachmentFile,
        mimeType: result.attachment.mimeType || 'application/octet-stream',
      };
    }
    throw new Error('Attachment could not be loaded');
  },

  loadThumbnail: async item => {
    const id = attachmentId(item);
    const url = await attachmentService.getAttachmentThumbnail(id);
    if (url) return url;
    // Fallback for images: load the full file when no thumbnail exists.
    // This handles legacy attachments without pre-generated thumbnails.
    if (item.mimeType?.startsWith('image/')) {
      try {
        const result = await attachmentService.getAttachment(id);
        if (
          result.success &&
          result.attachment?.attachmentFile &&
          result.attachment?.mimeType
        ) {
          return getViewableFileUrl(
            result.attachment.attachmentFile,
            result.attachment.mimeType,
          );
        }
      } catch (err) {
        console.error(`Failed to load full file for attachment ${id}:`, err);
      }
    }
    return null;
  },

  download: item => {
    void attachmentService.downloadAttachment(attachmentId(item));
  },
};
